<template>
    <transition>
        <div class="container-fluid page-content">
            <div class="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center page-header-row gap-3">
                <div>
                    <h4 class="page-title text-slate-800 fw-bold mb-1">Company</h4>
                    <nav aria-label="breadcrumb">
                        <ol class="breadcrumb mb-0">
                            <li class="breadcrumb-item"><a href="/" class="text-muted"><i class="fas fa-home me-1"></i>Home</a></li>
                            <li class="breadcrumb-item"><a href="javascript:void(0);" class="text-muted">Settings</a></li>
                            <li class="breadcrumb-item active text-indigo fw-medium" aria-current="page">Company List</li>
                        </ol>
                    </nav>
                </div>
                <div>
                    <button type="button" class="btn btn-premium d-flex align-items-center gap-2" @click="toggleModal" v-if="permission['company-create']">
                        <i class="fas fa-plus"></i> Add New Company
                    </button>
                </div>
            </div>

            <div class="row">
                <div class="col-12">
                    <div class="card border-0 shadow-sm table-card">
                        <div class="card-body p-0">
                            <div class="table-responsive">
                                <table id="basic-datatable" class="table table-premium mb-0"
                                    v-if="!loading && items.length > 0">
                                    <thead>
                                        <tr>
                                            <th>SL</th>
                                            <th>Company Name</th>
                                            <th>Logo</th>
                                            <th>Contact Person Name</th>
                                            <th>Contact Person Number</th>
                                            <th>Status</th>
                                            <th>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="(item, i) in items" :key="i">
                                            <td>{{ i + 1 }}</td>
                                            <td class="fw-bold text-slate-800">{{ item.name }}</td>
                                            <td>
                                                <div class="image-avatar-container" v-if="item.logo">
                                                    <img :src="item.logo" class="image-avatar" alt="Company Logo">
                                                </div>
                                            </td>
                                            <td>{{ item.contact_person_name }}</td>
                                            <td>{{ item.contact_person_number }}</td>
                                            <td>
                                                <span v-if="item.status==1" class="badge-role badge-role-active">Active</span>
                                                <span v-if="item.status==0" class="badge-role badge-role-inactive">Inactive</span>
                                            </td>
                                            <td>
                                                <div class="dropdown">
                                                    <a href="#" class="dropdown-toggle arrow-none card-drop premium-dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false">
                                                        <i class="fas fa-ellipsis-v"></i>
                                                    </a>
                                                    <div class="dropdown-menu dropdown-menu-end premium-dropdown-menu">
                                                        <a href="javascript:void(0);" class="dropdown-item" @click="edit(item)" v-if="permission['company-edit']">
                                                            <i class="fas fa-edit me-2 text-warning"></i>Edit
                                                        </a>
                                                        <a href="javascript:void(0);" class="dropdown-item" @click="deleteItem(item)" v-if="permission['company-delete']">
                                                            <i class="fas fa-trash-alt me-2 text-danger"></i>Remove
                                                        </a>
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>

                                <!-- Empty State -->
                                <div class="empty-state text-center py-5" v-if="!loading && items.length === 0">
                                    <i class="fas fa-building fa-3x text-muted mb-3"></i>
                                    <h5 class="text-muted fw-semibold">No Companies Found</h5>
                                    <p class="text-muted mb-0">Get started by adding your first company.</p>
                                </div>

                                <!-- Loading State -->
                                <div class="text-center py-5" v-if="loading">
                                    <div class="spinner-premium" role="status">
                                        <span class="visually-hidden">Loading...</span>
                                    </div>
                                    <p class="text-muted mt-3 mb-0">Loading companies...</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <Modal @close="toggleModal()" :modalActive="modalActive">
                <div class="modal-content border-0 shadow-lg modal-premium">
                    <div class="modal-header">
                        <h4 class="fw-bold m-0">{{ editMode ? 'Update Company' : 'Create New Company' }}</h4>
                        <button @click="toggleModal()" type="button" class="btn-close-custom">✕</button>
                    </div>
                    <form @submit.prevent="submitForm()" enctype="multipart/form-data">
                        <div class="modal-body">
                            <div class="row">
                                <div class="col-md-8">
                                    <div class="form-group">
                                        <div class="mb-3">
                                            <label for="name" class="form-label-custom text-slate-700 fw-semibold mb-2">Company name *</label>
                                            <input type="text" class="form-control-custom"
                                                :class="{'is-invalid': errors.name}"
                                                @keypress="onkeyPress('name')" v-model="form.name" id="name"
                                                placeholder="Company name" autocomplete="off">
                                            <div class="invalid-feedback" v-if="errors.name">
                                                {{errors.name[0]}}
                                            </div>
                                        </div>
                                    </div>
                                    <div class="row">
                                        <div class="form-group col-md-6">
                                            <div class="mb-3">
                                                <label for="contact_person_name" class="form-label-custom text-slate-700 fw-semibold mb-2">Contact person name *</label>
                                                <input type="text" class="form-control-custom"
                                                    :class="{'is-invalid': errors.contact_person_name}"
                                                    @keypress="onkeyPress('contact_person_name')"
                                                    v-model="form.contact_person_name" id="contact_person_name"
                                                    placeholder="Contact person name">
                                                <div class="invalid-feedback" v-if="errors.contact_person_name">
                                                    {{errors.contact_person_name[0]}}
                                                </div>
                                            </div>
                                        </div>
                                        <div class="form-group col-md-6">
                                            <div class="mb-3">
                                                <label for="contact_person_number" class="form-label-custom text-slate-700 fw-semibold mb-2">Contact person number *</label>
                                                <input type="text" class="form-control-custom"
                                                    :class="{'is-invalid': errors.contact_person_number}"
                                                    @keypress="onkeyPress('contact_person_number')"
                                                    v-model="form.contact_person_number" id="contact_person_number"
                                                    placeholder="Contact person number">
                                                <div class="invalid-feedback" v-if="errors.contact_person_number">
                                                    {{errors.contact_person_number[0]}}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="form-group">
                                        <div class="mb-3">
                                            <label for="Address" class="form-label-custom text-slate-700 fw-semibold mb-2">Address *</label>
                                            <input type="text" class="form-control-custom"
                                                :class="{'is-invalid': errors.address}"
                                                @keypress="onkeyPress('address')" v-model="form.address" id="Address"
                                                placeholder="1234 Main St">
                                            <div class="invalid-feedback" v-if="errors.address">
                                                {{errors.address[0]}}
                                            </div>
                                        </div>
                                    </div>
                                    <div class="row">
                                        <div class="form-group col-md-6">
                                            <div class="mb-3">
                                                <label class="form-label-custom text-slate-700 fw-semibold mb-2">Company Logo (128X128) *</label>
                                                <div class="file-upload-wrapper">
                                                    <input type="file" name="logo" @change="onImageChange" class="file-upload-input" />
                                                    <div class="file-upload-content">
                                                        <i class="fas fa-cloud-upload-alt fa-2x text-indigo mb-2"></i>
                                                        <p class="mb-0 text-muted">Click to upload logo</p>
                                                    </div>
                                                </div>
                                                <div class="invalid-feedback" v-if="errors.image">
                                                    {{errors.image[0]}}
                                                </div>
                                            </div>
                                        </div>

                                        <div class="form-group col-md-6">
                                            <div class="mb-3">
                                                <label for="active" class="form-label-custom text-slate-700 fw-semibold mb-2">Status</label>
                                                <select class="form-select-custom"
                                                    :class="{'is-invalid': errors.status}"
                                                    v-model="form.status"
                                                    @change="onkeyPress('status')" id="active">
                                                    <option value="1">Active</option>
                                                    <option value="0">Inactive</option>
                                                </select>
                                                <div class="invalid-feedback" v-if="errors.status">
                                                    {{errors.status[0]}}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div class="col-md-4">
                                    <div class="modal-image-preview-container">
                                        <p class="form-label-custom text-slate-700 fw-semibold mb-2">Photo Preview</p>
                                        <img :src="logoPreview" v-if="logoPreview" class="preview-image">
                                        <div v-if="editMode & !logoPreview">
                                            <img :src="form.logo" v-if="form.logo" class="preview-image">
                                        </div>
                                        <div v-if="!logoPreview && !(editMode && form.logo)" class="preview-placeholder">
                                            <i class="fas fa-image fa-3x text-muted"></i>
                                            <p class="text-muted mt-2 mb-0">No image selected</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="modal-footer">
                            <button type="button" class="btn btn-outline-premium" @click="toggleModal()">Cancel</button>
                            <button type="submit" class="btn btn-premium" :disabled="disabled">
                                <span v-show="isSubmit">
                                    <i class="fas fa-spinner fa-spin me-1"></i>
                                </span>{{ editMode ? 'Update Company' : 'Create Company' }}
                            </button>
                        </div>
                    </form>
                </div>
            </Modal>
        </div>
    </transition>

</template>
<script>
import Modal from "./../helper/Modal";
import { ref, onMounted } from "vue";
import Form from 'vform'
import axios from 'axios';
export default {
    name: 'Company',
    components: {},
    data() {
        return {
            isSubmit: false,
            showModal: false,
            editMode: false,
            disabled: false,
            modalActive: false,
            errors: {},
            logoPreview: '',
            btn: 'Create',
            loading: true,
            items: [],
            form: new Form({
                id: '',
                name: '',
                logo: '',
                address: '',
                contact_person_name: '',
                contact_person_number: '',
                status: 1,
            }),
        };
    },
    components: {
        Modal,
    },
    methods: {
        toggleModal: function () {
            this.modalActive = !this.modalActive;
            if (!this.modalActive) {
                this.editMode = false;
                this.btn = 'Create';
            }
            this.errors = '';
            this.isSubmit = false;
            this.form.reset();
            console.log('then', this.isSubmit)
        },
        fetchIndexData() {
            axios.get(this.apiUrl + '/companies', this.headers)
                .then((res) => {
                    this.items = res.data.data;
                    //console.log('companies res',res.data.data);
                })
                .catch((response) => {
                    //console.log('companies => ',response.data) 
                }).finally((ress) => {
                    this.loading = false;
                });
        },
        edit: function (item) {
            this.btn = 'Update';
            this.editMode = true;
            this.toggleModal();
            this.form.fill(item);
            this.logoPreview = '';
        },
        checkForm: function (e) {

        },
        submitForm: function (e) {
            this.isSubmit = true;
            this.disabled = true;
            const formData = new FormData();
            formData.append('name', this.form.name);
            formData.append('address', this.form.address);
            formData.append('contact_person_name', this.form.contact_person_name);
            formData.append('contact_person_number', this.form.contact_person_number);
            formData.append('status', this.form.status);
            if (this.editMode) {
                formData.append('_method', 'put');
                if (this.logoPreview) {
                    this.form.logo ? formData.append('logo', this.form.logo, this.form.logo.name) : '';
                }
                var postEvent = axios.post(this.apiUrl + '/companies/' + this.form.id, formData, this.headers);
            } else {
                this.form.logo ? formData.append('logo', this.form.logo, this.form.logo.name) : '';
                var postEvent = axios.post(this.apiUrl + '/companies', formData, this.headers);
            }
            postEvent.then(res => {
                this.isSubmit = false;
                this.disabled = false;
                if (res.status == 200) {
                    this.toggleModal();
                    this.fetchIndexData();
                    this.$toast.success(res.data.message);
                } else {
                    this.$toast.error(res.data.message);
                }
                console.log('then', this.isSubmit)
            }).catch(err => {
                this.isSubmit = false;
                this.disabled = false;
                this.$toast.error(err.response.data.message);
                if (err.response.status == 422) {
                    this.errors = err.response.data.errors
                }
                console.log('catch', this.isSubmit)
            });
        },

        onImageChange(e) {
            this.form.logo = e.target.files[0]
            let files = e.target.files || e.dataTransfer.files;
            if (!files.length)
                return;

            this.createImage(files[0]);

        },
        createImage(file) {
            let reader = new FileReader();
            let vm = this;
            reader.onload = (e) => {
                vm.logoPreview = e.target.result;
            };
            reader.readAsDataURL(file);
        },
        validation: function (...fiels) {
            var obj = new Object();
            var validate = false;
            for (var k in fiels) {     // Loop through the object  
                for (var j in this.form) {
                    if ((j == fiels[k]) && (!this.form[j])) {
                        obj[fiels[k]] = fiels[k].replace("_", " ") + ' field is required';  // Delete obj[key]; 
                        this.errors = { ...this.errors, ...obj };
                    } else {
                        validate = false;
                    }
                }
            }
            // var obj = new Object();
            // obj[fiels] = fiels.replace("_", " ")+' field is required';  
            // this.errors = {...this.errors, ...obj}; 
        },
        onkeyPress: function (field) {
            for (var k in this.errors) {     // Loop through the object
                if (k == field) {      // If the current key contains the string we're looking for 
                    delete this.errors[k];  // Delete obj[key];
                }
            }
        },
        deleteItem: function (item) {
            this.$swal({
                title: 'Are you sure?',
                text: "You want delete this item!",
                showCancelButton: true,
                confirmButtonCategory: '#3085d6',
                cancelButtonCategory: '#d33',
                confirmButtonText: 'Yes, delete it!'
            }).then((result) => {
                if (result.value) {
                    axios.delete(this.apiUrl + '/companies/' + item.id, this.headers)
                        .then(res => {
                            if (res.status == 200) {
                                this.fetchIndexData();
                                this.$toast.success(res.data.message);
                            } else {
                                this.$toast.error(res.data.message);
                            }
                            console.log(res.data)
                        }).catch(err => {
                            this.$toast.error(err.response.data.message);
                        })
                } else {
                    // this.$toast.error(`Hey! I'm here`);
                    // this.$toast.warning(`Hey! I'm here`);
                    // this.$toast.info(`Hey! I'm here`)
                }
            });
        }
    },
    created() {
        this.fetchIndexData();
    },
    destroyed() { },
    mounted() {
        window.scrollTo(0, 0);
    },
    computed: {
        permission() {
            let pname = this.$route.meta.parent_module;
            let module_name = this.$route.meta.module_name;
            let path_name = this.$route.path; 
            let data = '';
            if(this.$route.meta.parent_module){
                data = this.$store.getters.userAllPermissions[pname][0].children[path_name]
            }else{
                data = this.$store.getters.userAllPermissions[module_name][0].other_actions; 
            } 
            return data;
        },
    }
}
</script>